import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import { ENABLE_ORDER_TRACKER_RA_IR } from '#client/constants/experimentsFlags';

import {
  ALLOWED_ORDER_TASKS, CHIP_COLORS, ORDER_TASKS_UPSELLS, TASK_LINK_CONDITIONS,
  WFC_ADVANCED_UPSELL
} from './constants';

// Entitlement type constants
const ENTITLEMENT_WFC = 'worry_free_service';
const ENTITLEMENT_WFC_BASIC = 'worry_free_service_basic';
const ENTITLEMENT_WFC_ADVANCED = 'worry_free_service_advanced';

// Banking application statuses that should hide the banking task from OrderTracker
const FINAL_BANKING_STATUSES = [
  'approved',
  'denied',
  'canceled',
  'expired',
  'not_qualified',
  'closed'
];

const RA_IR_TASK_TYPES = ['registered_agent_service', 'wfg_initial_report'];
const IR_ALLOWED_JURISDICTIONS = ['AK', 'CA'];
const NEXT_UP_LLC_IN_APP_KEYS = ['finish-your-formation'];
const NEXT_UP_LLC_TOPICS = ['formation-update'];
const FORMATION_TASK_TYPES = ['llc_formation', 'corp_formation'];

const isIncompleteWithFormationBlocked = (item, _, { isFormationTaskIncomplete }) => (
  item.status === 'incomplete' && isFormationTaskIncomplete
);

const isIncompleteWithoutFormation = (item, _, { hasFormationTask, formationStatus }) => (
  item.status === 'incomplete' && !hasFormationTask && formationStatus !== 'formed'
);

const PREREQUISITE_TOOLTIP_CONFIG = {
  ein_creation: [
    {
      tooltipMessageId: 'dashboard.orderTracker.einPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationBlocked
    },
    {
      tooltipMessageId: 'dashboard.orderTracker.formationRequiredTooltip',
      shouldShow: isIncompleteWithoutFormation
    }
  ],
  business_license_report: [
    {
      tooltipMessageId: 'dashboard.orderTracker.blrPrerequisiteTooltip',
      shouldShow: isIncompleteWithFormationBlocked
    },
    {
      tooltipMessageId: 'dashboard.orderTracker.formationRequiredTooltip',
      shouldShow: isIncompleteWithoutFormation
    }
  ],
  registered_agent_service: [
    {
      tooltipMessageId: 'dashboard.orderTracker.raPrerequisiteTooltip',
      shouldShow: (item) => item.status === 'incomplete'
    }
  ]
};

/**
 * Check if we should fetch banking application status for this task
 * @param {object} item - Order task item
 * @returns {boolean} Whether to fetch banking application status
 */
export function shouldFetchBankingStatus(item) {
  return item.type === 'zb_banking_preferred' && item.status === 'pending';
}

/**
 * Check if a banking task should be hidden from OrderTracker
 * @param {object} item - Order task item
 * @param {string} bankingApplicationStatus - Banking application status
 * @returns {boolean} Whether to hide this banking task
 */
export function shouldHideBankingTask(item, bankingApplicationStatus) {
  if (!bankingApplicationStatus) {
    return false;
  }
  return item.type === 'zb_banking_preferred'
         && FINAL_BANKING_STATUSES.includes(bankingApplicationStatus);
}

/**
 * Returns filtered and sorted order task items for display in the OrderTracker and EnterpriseOrderTracker.
 * @param {Array} orderTasks - Raw order tasks from the API
 * @param {object} options
 * @param {boolean} options.enableRaIr - Whether to include RA and IR task types (experiment flag)
 * @param {boolean} options.includeComplete - Whether to include tasks with complete status (used by EnterpriseOrderTracker)
 * @returns {Array} Filtered/sorted order tasks with normalized status and chip color
 * Note: registered_agent_service with status 'active' is normalized to 'complete' before filtering, so this could
 * be shown in EnterpriseOrderTracker.
 */
export function getTimelineItems(orderTasks, { enableRaIr = false, includeComplete = false } = {}) {
  const validStatuses = Object.keys(CHIP_COLORS);
  return orderTasks
    .map((item) => {
      // registered_agent_service with 'active' means RA is live — normalize to complete before filtering
      const normalizedStatus = item.type === 'registered_agent_service' && item.status === 'active'
        ? 'complete'
        : item.status;
      return { ...item, status: normalizedStatus };
    })
    .filter((item) => {
      if (!ALLOWED_ORDER_TASKS.includes(item.type)) return false;
      if (!validStatuses.includes(item.status)) return false;
      // Needed for EnterpriseOrderTracker where we show complete tasks
      if (!includeComplete && item.status === 'complete') return false;
      if (RA_IR_TASK_TYPES.includes(item.type) && !enableRaIr) return false;
      if (item.type === 'wfg_initial_report' && !IR_ALLOWED_JURISDICTIONS.includes(item.jurisdictionCode)) return false;
      return true;
    })
    .map((item) => ({ ...item, color: CHIP_COLORS[item.status] }))
    .sort((a, b) => ALLOWED_ORDER_TASKS.indexOf(a.type) - ALLOWED_ORDER_TASKS.indexOf(b.type));
}

export function useGetTimelineItems(orderTasks) {
  const enableRaIr = useExperimentFlag(ENABLE_ORDER_TRACKER_RA_IR, false);
  return getTimelineItems(orderTasks, { enableRaIr });
}

/**
 * Resolves a NextUp CTA URL for a given order task.
 * NextUp is only used as source of truth for LLC formation.
 * @param {object} item - Order task item
 * @param {Array} nextUpCards - NextUp cards collection
 * @returns {string} Matching NextUp ctaUrl or empty string
 */
export function getNextUpUrlForTask(item, nextUpCards = []) {
  if (
    item?.type !== 'llc_formation'
    || !Array.isArray(nextUpCards)
    || nextUpCards.length === 0
  ) {
    return '';
  }

  const matchedCard = nextUpCards.find((card) => {
    if (typeof card?.ctaUrl !== 'string') {
      return false;
    }
    return NEXT_UP_LLC_IN_APP_KEYS.includes(card.inAppKey)
      || NEXT_UP_LLC_TOPICS.includes(card.topic);
  });

  return matchedCard?.ctaUrl || '';
}

export function getTimelineItemsWithRedirectLinks(
  orderTasks,
  getCheckoutCompleteUrl,
  nextUpCards = [],
  { formationStatus } = {}
) {
  if (!getCheckoutCompleteUrl) {
    return orderTasks.map((item) => ({
      ...item,
      hasExternalLink: false,
      linkTo: ''
    }));
  }

  const hasFormationTask = orderTasks.some(
    (t) => FORMATION_TASK_TYPES.includes(t.type)
  );
  const isFormationTaskIncomplete = orderTasks.some(
    (t) => FORMATION_TASK_TYPES.includes(t.type) && t.status === 'incomplete'
  );
  const tooltipContext = { hasFormationTask, isFormationTaskIncomplete, formationStatus };

  return orderTasks.map((item) => {
    const redirectUrlByNextUp = getNextUpUrlForTask(item, nextUpCards);
    const redirectUrlByType = getCheckoutCompleteUrl(item.type);
    const serviceCode = item.data?.service;
    const redirectUrlByService = serviceCode ? getCheckoutCompleteUrl(serviceCode) : null;
    const redirectUrl = redirectUrlByNextUp || redirectUrlByType || redirectUrlByService || '';
    const conditionFn = TASK_LINK_CONDITIONS[item.type];
    const hasExternalLink = conditionFn
      ? conditionFn(item, orderTasks, { formationStatus })
      : Boolean(redirectUrl);
    const tooltipCandidates = PREREQUISITE_TOOLTIP_CONFIG[item.type] || [];
    const matchedTooltip = tooltipCandidates.find(
      (c) => c.shouldShow(item, hasExternalLink, tooltipContext)
    );
    const hoverTooltipMessageId = matchedTooltip?.tooltipMessageId;

    return {
      ...item,
      hasExternalLink,
      ...(hoverTooltipMessageId && { hoverTooltipMessageId }),
      linkTo: item.status === 'incomplete' && hasExternalLink ? redirectUrl : ''
    };
  });
}

export function getUpsellItems(type, entitlements, jurisdictionCode) {
  if (!entitlements) {
    return [];
  }

  const typeUpsells = ORDER_TASKS_UPSELLS[type] || [];
  const upsellsToProcess = [...typeUpsells]; // Create a copy to work with

  // Special handling for worry-free service upsells
  const hasWorryfreeService = entitlements.has(ENTITLEMENT_WFC)
  || entitlements.has(ENTITLEMENT_WFC_BASIC);
  const hasWorryfreeAdvanced = entitlements.has(ENTITLEMENT_WFC_ADVANCED);

  // If user has worry_free_service but not worry_free_advanced, replace WFC with WFC Advanced
  if (hasWorryfreeService && !hasWorryfreeAdvanced) {
    const wfcIndex = upsellsToProcess.findIndex((upsell) => upsell.entitlement === ENTITLEMENT_WFC);
    if (wfcIndex !== -1) {
      // Replace WFC with WFC Advanced at the same position
      upsellsToProcess[wfcIndex] = WFC_ADVANCED_UPSELL;
    }
  }

  // Now filter out upsells the user already has
  let filteredUpsells = upsellsToProcess.filter(
    (upsell) => !entitlements.has(upsell.entitlement)
  );

  // Filter out upsells that are not allowed for the jurisdiction
  filteredUpsells = filteredUpsells.filter((upsell) => {
    if (upsell.allowedJurisdictions) {
      return upsell.allowedJurisdictions.includes(jurisdictionCode);
    }
    return true;
  });

  // Limit to max 3 upsells
  return filteredUpsells.slice(0, 3);
}

export function getTitleMessageSuffix(item) {
  if (item.type === 'change_of_registered_agent' && item.jurisdictionCode) {
    return ` - ${item.jurisdictionCode}`;
  }
  return '';
}
